import React, { useState, useEffect, useContext, Suspense } from "react";
import Input from "components/input";
import MealSeries from "components/mealSeries";
import ActiveStepIndicator from "components/shared/activeStepIndicator";
import { mealTimings } from "utils/constants/onboardingStep";
import { MobXProviderContext } from "mobx-react";
import PropagateLoader from "react-spinners/PropagateLoader";
import classNames from "classnames";

const TimePicker = React.lazy(() => import("components/timepicker"));

const Loading = () => (
  <div className="d-row min-h-screen justify-center items-center">
    <PropagateLoader color={"#DA2D8B"} loading={true} size={15} />
  </div>
);

const Step2 = ({ disableButton }) => {
  const { onboardingStore } = useContext(MobXProviderContext);

  const [selectedMealArray, setSelectedMealArray] = useState(
    onboardingStore.selectedMealArray || {}
  );
  const [showTimePicker, setShowTimePicker] = useState(
    selectedMealArray?.isPredefinedTime === false
  );

  useEffect(() => {
    const enable =
      Object.keys(selectedMealArray).length > 0 &&
      selectedMealArray?.meal_name !== "" &&
      selectedMealArray?.meal !== "" &&
      selectedMealArray?.target_time !== "";

    disableButton(!enable);

    onboardingStore.handleMealSeriesData(selectedMealArray);
  }, [selectedMealArray]);

  const handleOtherMealName = (name) => {
    const newMealObject = {
      meal_name: name,
      meal: "other",
      target_time: "",
      isPredefinedTime: false,
    };

    setSelectedMealArray(newMealObject);
  };

  const handleMealChange = (meal) => {
    setShowTimePicker(false);
    const newMealObject = {
      meal_name: meal !== "other" ? meal : "",
      meal: meal,
      target_time: "",
      isPredefinedTime: true,
    };

    setSelectedMealArray(newMealObject);
  };

  const handleTimeInput = (time, isPredefined) => {
    setShowTimePicker(!isPredefined);

    setSelectedMealArray((prevMealArray) => ({
      ...prevMealArray,
      target_time: time,
      isPredefinedTime: isPredefined,
    }));
  };

  return (
    <>
      <ActiveStepIndicator step={2} />

      <div className="flex-col flex gap-6">
        <div className="screen-xs:w-full-235">
          <p className="expected-bag-text font-inter-semibold">
            Which meal are you looking to bring to your team?
          </p>
          <p className="font-inter-normal text-slate-700 text-base leading-tight">
            Let's begin with one meal plan. You can always add more later in
            your dashboard.
          </p>
        </div>
      </div>

      <div className="py-4 gap-6 screen-lg:flex screen-md:flex screen-sm:flex-col">
        <MealSeries
          handleMealChange={handleMealChange}
          mealSelected={selectedMealArray}
        />
      </div>

      {selectedMealArray?.meal !== "other" &&
        Object.keys(selectedMealArray)?.length !== 0 && (
          <>
            <div className="expected-bag-text screen-xs:w-full-235 font-inter-semibold">
              What is the ideal time for your {selectedMealArray?.meal}{" "}
              delivery?
            </div>

            <div className="gap-3 my-6 screen-lg:flex screen-md:flex screen-sm:flex-col space-y-4 screen-lg:space-y-0 screen-md:space-y-0">
              {mealTimings[selectedMealArray?.meal].map((time, index) => (
                <div
                  key={index}
                  className={classNames(
                    "w-full screen-lg:w-24 screen-md:w-24 h-[52px] rounded-md justify-center items-center flex hover:cursor-pointer font-inter-medium leading-tight",
                    {
                      "bg-indigo-500 text-white":
                        selectedMealArray?.target_time == time &&
                        selectedMealArray?.isPredefinedTime,
                      "text-slate-700 bg-slate-50 hover:bg-indigo-500 hover:text-white":
                        !(
                          selectedMealArray?.target_time == time &&
                          selectedMealArray?.isPredefinedTime
                        ),
                    }
                  )}
                  onClick={() => {
                    handleTimeInput(time, true);
                    setShowTimePicker(false);
                  }}
                >
                  {time}
                </div>
              ))}

              <Suspense fallback={<Loading />}>
                <div
                  className="w-full screen-lg:w-36 screen-md:w-36 h-[52px] bg-slate-50 rounded-md justify-center items-center font-inter-medium text-indigo-500 flex hover:cursor-pointer"
                  onClick={() => {
                    setShowTimePicker(true);
                    setSelectedMealArray((prevMealArray) => ({
                      ...prevMealArray,
                      isPredefinedTime: false,
                    }));
                  }}
                >
                  {showTimePicker ? (
                    <TimePicker
                      ideal_time={selectedMealArray?.target_time}
                      datetime={handleTimeInput}
                      meal_name={selectedMealArray?.meal}
                      handleShowModal={true}
                    />
                  ) : (
                    "Select Your Time"
                  )}
                </div>
              </Suspense>
            </div>
          </>
        )}

      {selectedMealArray?.meal === "other" && (
        <div className="w-full mb-6">
          <Input
            text={`What would you like to name this meal?*`}
            placeholder={"ex. Town Hall Dinner"}
            otherMealName={handleOtherMealName}
            mealName={selectedMealArray?.meal_name}
            isCompulsary={true}
          />
        </div>
      )}

      {selectedMealArray?.meal_name !== "" &&
        selectedMealArray?.meal === "other" && (
          <>
            <div className="expected-bag-text screen-xs:w-full-235 font-inter-semibold">
              What is the ideal time for your {selectedMealArray?.meal_name}{" "}
              delivery?
            </div>

            <Suspense fallback={<Loading />}>
              <div className="w-full screen-lg:w-32 screen-md:w-32 my-6 flex bg-slate-50 rounded-md font-inter-medium">
                <TimePicker
                  ideal_time={selectedMealArray?.target_time}
                  datetime={handleTimeInput}
                  meal_name="other"
                  handleShowModal={true}
                />
              </div>
            </Suspense>
          </>
        )}
    </>
  );
};

export default Step2;
